/**
 * 从面板进按宠物区分的页面、要把当前宠物换成面板上那只时（MapHomeView 的 enterPet，C84A-MAP-MAIL-SCOPE-01）：
 * selectPet 当场写 sessionStorage、清缓存，地图却要等跳转（过渡）落地才卸下。这一小段里地图上还可能有别的活：
 * - 上一次提交还没跑的副作用（useQuery 在 useEffect 里 setOptions；React 提交后先让出给绘制，副作用下一轮才跑）；
 * - 一次还没画的急更新（时钟跳一下、回到前台等，比过渡优先，先画）。
 * 它们看到的都是清空的缓存：多读一遍世界状态、家园快照……急更新还会先画出“正在找 TA 在哪…”、把地图拆掉
 * （2026-09-27 查 C84A 偶发失败时两条路都做成了必现，见 tests/claude-6c2b-map-mail-scope.test.tsx 的“竞态”几条）。
 * 所以 enterPet 先记下要换成哪只；这次渲染的当前宠物还不是它（= 正在离开）时，地图上的查询一律冻住：
 * - 不读：enabled 用函数，跑到才判断——上一次提交留下的副作用拿的是旧选项，也拦得住；
 * - 不画加载态：数据沿用同一把键的上一份（placeholderData；键不同就不沿用，绝不拿别的宠物的顶替）。
 * 换成了（跳转没发生、这一页原地换了宠物，例如按住 Ctrl 点、浏览器另开标签）那次渲染就不再冻，照常按新的那只重读；记录随即清掉。
 */
import { createContext, useContext, useLayoutEffect, useRef, type RefObject } from "react";
import { hashKey, type QueryKey } from "@tanstack/react-query";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";

type Entering = RefObject<string | null>;

/** LiveMapHome 把这张地图的记录传给里面的钩子；演示与地图以外的地方没有，永远不冻。 */
export const MapLeaveContext = createContext<Entering | null>(null);

/** LiveMapHome 用：这张地图“要换成哪只”的记录；当前宠物真的换成它的那次提交里清掉。 */
export function useEnteringRecord(currentPetId: string | null): Entering {
  const entering = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (entering.current === currentPetId) entering.current = null;
  }, [currentPetId]);
  return entering;
}

/** 这次渲染（当前宠物是 currentPetId）的地图是不是正在离开。返回函数：跑到才判断。 */
export function leavingFor(entering: Entering | null, currentPetId: string | null): () => boolean {
  return () => entering?.current != null && entering.current !== currentPetId;
}

/** 地图里的钩子用：同上，当前宠物取这次渲染的家庭上下文。 */
export function useMapLeaving(): () => boolean {
  return leavingFor(useContext(MapLeaveContext), useOptionalCurrentHousehold()?.pet?.pet_id ?? null);
}

/** enterPet 用：在调 selectPet 之前记下要换成哪只。 */
export function useMarkEntering(): (petId: string) => void {
  const entering = useContext(MapLeaveContext);
  return (petId) => {
    if (entering) entering.current = petId;
  };
}

/** 一条地图查询在离开时冻住：不读；数据沿用同一把键的上一份。展开进 useQuery 的选项里（替掉原来的 enabled）。 */
export function frozenWhileLeaving<TData>(leaving: () => boolean, enabled: boolean, queryKey: QueryKey) {
  const hash = hashKey(queryKey);
  return {
    enabled: () => enabled && !leaving(),
    placeholderData: (previous: TData | undefined, previousQuery: { queryKey: QueryKey } | undefined): TData | undefined =>
      leaving() && previousQuery !== undefined && hashKey(previousQuery.queryKey) === hash ? previous : undefined,
  };
}
